import { clearToken, getToken, setToken } from './auth.js';
import { forceLogout } from './authBridge.js';
import { setLastForbidden } from './forbiddenState.js';
import { navigateTo } from './navigation.js';

// Puerto real de la API en dev (ver context.md §4) — sobreescribible con VITE_API_URL.
const API_BASE_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3000';

export interface ApiErrorPayload {
  error: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Cualquier 403 real del backend (cuenta desactivada a media sesión, o una
// acción sin permiso — ej. PATCH /tickets/:id/responsables con un rol no
// autorizado) se muestra como pantalla amigable en /sin-acceso, nunca como
// el JSON crudo. CUENTA_INACTIVA es el único caso donde además se limpia el
// token: ahí la sesión entera murió en el backend, no solo esta acción.
function handleForbidden(payload: Partial<ApiErrorPayload> | null): void {
  const code = payload?.code ?? 'FORBIDDEN';
  setLastForbidden({ code, message: payload?.error ?? 'No tienes permiso para realizar esta acción.' });
  // forceLogout() limpia storage Y el estado de React de AuthProvider —
  // clearToken() solo no bastaría, useAuth().user quedaría obsoleto.
  if (code === 'CUENTA_INACTIVA') forceLogout();
  navigateTo('/sin-acceso', { replace: true });
}

let refreshPromise: Promise<string | null> | null = null;

// El accessToken dura 15 min (ver context.md §7.1) y hasta ahora nada lo renovaba:
// pasados 15 min, toda petición autenticada fallaba con 401 en silencio (useQuery
// no muestra ese error para lookups de fondo como catálogos/usuarios), dejando los
// <select> vacíos aunque el endpoint funcione. Aquí se refresca una sola vez usando
// la cookie httpOnly de refreshToken (POST /auth/refresh) y se reintenta la petición.
// refreshPromise deduplica refrescos concurrentes: si varias peticiones reciben 401
// al mismo tiempo, todas esperan la MISMA llamada a /auth/refresh.
// Exportada para el silent refresh al montar la app (ver hooks/useAuth.tsx):
// mismo mecanismo, pero disparado proactivamente antes de decidir la ruta,
// no solo reactivamente tras un 401.
export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.status === 403) {
          const payload = (await res.json().catch(() => null)) as Partial<ApiErrorPayload> | null;
          handleForbidden(payload);
          return null;
        }
        if (!res.ok) return null;
        const body = (await res.json().catch(() => null)) as { accessToken?: string } | null;
        if (!body?.accessToken) return null;
        setToken(body.accessToken);
        return body.accessToken;
      } catch {
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

async function request<T>(path: string, init: RequestInit = {}, isRetry = false): Promise<T> {
  const token = getToken();

  const headers = new Headers(init.headers);
  // Fastify rechaza con 400/500 un Content-Type: application/json sin body
  // (FST_ERR_CTP_EMPTY_JSON_BODY) — solo declararlo cuando de verdad hay body.
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (res.status === 401 && !isRetry && path !== '/auth/refresh') {
    const newToken = await refreshAccessToken();
    if (newToken) return request<T>(path, init, true);
    clearToken();
  }

  if (res.status === 204) return undefined as T;

  const body: unknown = await res.json().catch(() => null);

  if (res.status === 403) {
    const payload = body as Partial<ApiErrorPayload> | null;
    handleForbidden(payload);
    throw new ApiError(payload?.error ?? 'Acceso denegado', 403, payload?.code);
  }

  if (!res.ok) {
    const payload = body as Partial<ApiErrorPayload> | null;
    throw new ApiError(payload?.error ?? 'Error de red', res.status, payload?.code);
  }

  return body as T;
}

// Cliente HTTP compartido — inyecta el JWT automáticamente en cada llamada.
export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),

  post: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
    }),

  patch: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'PATCH',
      ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
    }),

  delete: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'DELETE',
      ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
    }),
};

// Upload de archivo con progreso real (bytes enviados) — usa XMLHttpRequest
// porque fetch() no expone eventos de progreso de subida. No pasa por
// request(): un FormData necesita que el navegador fije su propio
// Content-Type con boundary, nunca 'application/json'.
export function uploadArchivo<T>(path: string, file: File, onProgress?: (pct: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}${path}`);
    xhr.withCredentials = true;
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T);
      } else {
        const payload = body as Partial<ApiErrorPayload> | null;
        reject(new ApiError(payload?.error ?? 'Error de red', xhr.status, payload?.code));
      }
    };
    xhr.onerror = () => reject(new ApiError('Error de red', 0));

    const formData = new FormData();
    formData.append('file', file);
    xhr.send(formData);
  });
}

// Descarga autenticada — un <a href> normal no puede llevar el header
// Authorization, así que se trae como blob y se dispara la descarga sintética.
export async function descargarArchivo(path: string, nombreArchivo: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });

  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as Partial<ApiErrorPayload> | null;
    throw new ApiError(payload?.error ?? 'No fue posible descargar el archivo', res.status, payload?.code);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
